import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/App.css";
import "@/i18n";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BUProvider } from "@/contexts/BUContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { Toaster } from "@/components/ui/sonner";
import OfflineIndicator from "@/components/OfflineIndicator";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import MainLayout from "@/components/MainLayout";
import DashboardPage from "@/pages/DashboardPage";
import IncidentsPage from "@/pages/IncidentsPage";
import RisksPage from "@/pages/RisksPage";
import AuditsPage from "@/pages/AuditsPage";
import CAPAsPage from "@/pages/CAPAsPage";
import TrainingPage from "@/pages/TrainingPage";
import TrainingCurrencyPage from "@/pages/TrainingCurrencyPage";
import PoliciesPage from "@/pages/PoliciesPage";
import CompliancePage from "@/pages/CompliancePage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import BulletinsPage from "@/pages/BulletinsPage";
import SPIsPage from "@/pages/SPIsPage";
import MeetingsPage from "@/pages/MeetingsPage";
import ChangesPage from "@/pages/ChangesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import SurveyPage from "@/pages/SurveyPage";
import InspectorPortal from "@/pages/InspectorPortal";
import MobileHazardReport from "@/pages/MobileHazardReport";
import DigitalTwinPage from "@/pages/DigitalTwinPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import IntegrationConfigPage from "@/pages/IntegrationConfigPage";
import RegulatoryResourcesPage from "@/pages/RegulatoryResourcesPage";
import TrendsPage from "@/pages/TrendsPage";
import ScheduledReportsPage from "@/pages/ScheduledReportsPage";
import EquipmentPage from "@/pages/EquipmentPage";
import RegulatoryReportsPage from "@/pages/RegulatoryReportsPage";
// Asset Repository Pages
import OrganizationsPage from "@/pages/OrganizationsPage";
import AircraftPage from "@/pages/AircraftPage";
import EnginesPage from "@/pages/EnginesPage";
import ComponentsPage from "@/pages/ComponentsPage";
import GSEPage from "@/pages/GSEPage";
import AssetAnalyticsPage from "@/pages/AssetAnalyticsPage";
import DataSharingPage from "@/pages/DataSharingPage";
import MROIntegrationPage from "@/pages/MROIntegrationPage";
import DocumentsPage from "@/pages/DocumentsPage";
import DocumentTemplatesPage from "@/pages/DocumentTemplatesPage";
import BowtiePage from "@/pages/BowtiePage";
import WorkflowBuilderPage from "@/pages/WorkflowBuilderPage";
import AuditScenarioPage from "@/pages/AuditScenarioPage";
import AuditProgrammePage from "@/pages/AuditProgrammePage";
import EnterpriseAuditPage from "@/pages/EnterpriseAuditPage";
import OrganizationManagementPage from "@/pages/OrganizationManagementPage";
import ContractorPortalPage from "@/pages/ContractorPortalPage";
import RegulatorDashboardPage from "@/pages/RegulatorDashboardPage";
import EnterpriseUserManagementPage from "@/pages/EnterpriseUserManagementPage";
import LDAPSyncPage from "@/pages/LDAPSyncPage";
import FDMPage from "@/pages/FDMPage";
import FRMSPage from "@/pages/FRMSPage";
import AIRiskPage from "@/pages/AIRiskPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import TrainingModulesPage from "@/pages/TrainingModulesPage";
import TrainingCompliancePage from "@/pages/TrainingCompliancePage";
import AIChatbotPage from "@/pages/AIChatbotPage";
import ISMSPage from "@/pages/ISMSPage";
import BiometricMonitoringPage from "@/pages/BiometricMonitoringPage";
import CollaborationHubPage from "@/pages/CollaborationHubPage";
import GamificationPage from "@/pages/GamificationPage";
import FatigueAlertsPage from "@/pages/FatigueAlertsPage";
import WearableSettingsPage from "@/pages/WearableSettingsPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import AdminDataManagementPage from "@/pages/AdminDataManagementPage";
import BusinessUnitsPage from "@/pages/BusinessUnitsPage";
import BIDashboardPage from "@/pages/BIDashboardPage";
import InvestigationTemplatesPage from "@/pages/InvestigationTemplatesPage";
import SafetyCultureSurveyPage from "@/pages/SafetyCultureSurveyPage";
import WildlifeStrikePage from "@/pages/WildlifeStrikePage";
import ReportBuilderPage from "@/pages/ReportBuilderPage";
import EmergencyResponsePage from "@/pages/EmergencyResponsePage";
import LessonsLearnedPage from "@/pages/LessonsLearnedPage";
import GapAnalysisPage from "@/pages/GapAnalysisPage";
import RiskRadarPage from "@/pages/RiskRadarPage";
import RegulatoryAPIPage from "@/pages/RegulatoryAPIPage";
import MaintenancePage from "@/pages/MaintenancePage";
import CrewManagementPage from "@/pages/CrewManagementPage";
import IOSAAuditPage from "@/pages/IOSAAuditPage";
import AIQueryPage from "@/pages/AIQueryPage";
import PredictiveMaintenancePage from "@/pages/PredictiveMaintenancePage";
import GACAAuditPage from "@/pages/GACAAuditPage";
import InspectorModePage from "@/pages/InspectorModePage";
import AEEngagementPage from "@/pages/AEEngagementPage";
import ResourceAdequacyPage from "@/pages/ResourceAdequacyPage";
import SMSImplementationPlanPage from "@/pages/SMSImplementationPlanPage";
import GuidedTour from "@/components/GuidedTour";
import VoiceCommandListener from "@/components/VoiceCommandListener";
import OnboardingWizard from "@/components/OnboardingWizard";
import FirstPasswordResetModal from "@/components/FirstPasswordResetModal";
import ErrorBoundary from "@/components/ErrorBoundary";
import SupportWidget from "@/components/SupportWidget";
import PrivacyPolicyPage from "@/components/legal/PrivacyPolicyPage";
import TermsOfUsePage from "@/components/legal/TermsOfUsePage";
import DataProcessingPage from "@/components/legal/DataProcessingPage";
import AdminSupportPage from "@/pages/AdminSupportPage";
import AdminLogsPage from "@/pages/AdminLogsPage";
import PWAInstallPrompt, { OfflineIndicator as PWAOfflineIndicator } from "@/components/PWAInstallPrompt";

// Create a client for react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const AppContent = () => {
  const { isAuthenticated, loading, user, token, mustChangePassword } = useAuth();
  const [authMode, setAuthMode] = useState('login');
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [pageOptions, setPageOptions] = useState({});
  const [showMobileReport, setShowMobileReport] = useState(false);

  // Hash-based routing for legal pages so they render pre-auth and post-auth.
  const [legalHash, setLegalHash] = useState(
    typeof window !== 'undefined' ? (window.location.hash || '').replace(/^#/, '') : ''
  );
  useEffect(() => {
    const onHash = () => setLegalHash((window.location.hash || '').replace(/^#/, ''));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const openLegal = (page) => {
    window.location.hash = page ? `#${page}` : '';
  };

  const handleNavigate = (page, options = {}) => {
    setCurrentPage(page);
    setPageOptions(options);
    // Scroll main content to top when navigating
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTop = 0;
    window.scrollTo(0, 0);
  };

  const handleQuickReportSuccess = () => {
    setShowMobileReport(false);
    setCurrentPage('incidents');
  };

  // ── Legal pages take precedence over everything else (works pre-auth too)
  if (legalHash === 'privacy')          return <PrivacyPolicyPage  onBack={() => openLegal('')} />;
  if (legalHash === 'terms')            return <TermsOfUsePage     onBack={() => openLegal('')} />;
  if (legalHash === 'data-processing')  return <DataProcessingPage onBack={() => openLegal('')} />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="spinner w-10 h-10 mx-auto mb-4" />
          <p className="text-slate-400">Loading AeroGuard SMS...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authMode === 'register') {
      return <RegisterPage onSwitchToLogin={() => setAuthMode('login')} />;
    }
    return <LoginPage onSwitchToRegister={() => setAuthMode('register')} />;
  }

  // ── First-login password reset: block the whole app until the invited
  // user rotates their temporary password. Backend also enforces this via
  // scope=password_reset_only tokens.
  if (mustChangePassword) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 text-sm">
        <FirstPasswordResetModal />
        <span data-testid="first-password-locked-shell">
          Please set a new password to continue.
        </span>
      </div>
    );
  }

  // Inspector users get the dedicated read-only portal
  if (user?.role === 'inspector') {
    return (
      <div className="min-h-screen bg-slate-950">
        <InspectorPortal />
      </div>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage onNavigate={handleNavigate} />;
      case 'bulletins':
        return <BulletinsPage />;
      case 'incidents':
        return <IncidentsPage initialMode={pageOptions.mode} />;
      case 'risks':
        return <RisksPage initialMode={pageOptions.mode} />;
      case 'audits':
        return <AuditsPage initialMode={pageOptions.mode} />;
      case 'capas':
        return <CAPAsPage initialMode={pageOptions.mode} />;
      case 'spis':
        return <SPIsPage />;
      case 'meetings':
        return <MeetingsPage />;
      case 'changes':
        return <ChangesPage />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'digital-twin':
        return <DigitalTwinPage onNavigate={handleNavigate} />;
      case 'integrations':
        return <IntegrationsPage />;
      case 'integration-config':
        return <IntegrationConfigPage />;
      case 'trends':
        return <TrendsPage />;
      case 'scheduled-reports':
        return <ScheduledReportsPage />;
      case 'equipment':
        return <EquipmentPage />;
      case 'regulatory-reports':
        return <RegulatoryReportsPage />;
      case 'bowtie':
        return <BowtiePage />;
      case 'audit-scenario':
        return <AuditScenarioPage />;
      case 'audit-programme':
        return <AuditProgrammePage />;
      case 'enterprise-audit':
        return <EnterpriseAuditPage />;
      case 'org-management':
        return <OrganizationManagementPage />;
      case 'contractor-portal':
        return <ContractorPortalPage />;
      case 'regulator-dashboard':
        return <RegulatorDashboardPage />;
      case 'admin-support':
        return <AdminSupportPage />;
      case 'admin-logs':
        return <AdminLogsPage />;
      // Asset Repository Pages
      case 'organizations':
        return <OrganizationsPage />;
      case 'aircraft':
        return <AircraftPage />;
      case 'engines':
        return <EnginesPage />;
      case 'components':
        return <ComponentsPage />;
      case 'gse':
        return <GSEPage />;
      case 'asset-analytics':
        return <AssetAnalyticsPage />;
      case 'data-sharing':
        return <DataSharingPage />;
      case 'mro-integration':
        return <MROIntegrationPage />;
      case 'surveys':
        return <SurveyPage />;
      case 'training':
        return <TrainingPage />;
      case 'training-currency':
        return <TrainingCurrencyPage />;
      case 'policies':
        return <PoliciesPage initialMode={pageOptions.mode} />;
      case 'compliance':
        return <CompliancePage />;
      case 'documents':
        return <DocumentsPage />;
      case 'document-templates':
        return <DocumentTemplatesPage />;
      case 'workflow-builder':
        return <WorkflowBuilderPage />;
      case 'reports':
        return <ReportsPage />;
      case 'settings':
        return <SettingsPage />;
      case 'users':
      case 'user-management':
        return <EnterpriseUserManagementPage />;
      case 'ldap-sync':
        return <LDAPSyncPage />;
      case 'fdm':
        return <FDMPage />;
      case 'frms':
        return <FRMSPage />;
      case 'ai-risk':
        return <AIRiskPage />;
      case 'help-center':
        return <HelpCenterPage />;
      case 'training-modules':
        return <TrainingModulesPage />;
      case 'training-compliance':
        return <TrainingCompliancePage />;
      case 'ai-assistant':
        return <AIChatbotPage onNavigate={handleNavigate} />;
      case 'isms':
        return <ISMSPage />;
      case 'biometric':
        return <BiometricMonitoringPage />;
      case 'gamification':
        return <GamificationPage />;
      case 'fatigue-alerts':
        return <FatigueAlertsPage />;
      case 'collaboration':
        return <CollaborationHubPage />;
      case 'wearable-settings':
        return <WearableSettingsPage />;
      case 'notification-settings':
        return <NotificationSettingsPage />;
      case 'admin-data-management':
        return <AdminDataManagementPage />;
      case 'business-units':
        return <BusinessUnitsPage />;
      case 'bi-dashboard':
        return <BIDashboardPage />;
      case 'inspection-mode':
        return <InspectorPortal />;
      case 'regulatory-resources':
        return <RegulatoryResourcesPage />;
      case 'investigation-templates':
        return <InvestigationTemplatesPage />;
      case 'safety-culture-surveys':
        return <SafetyCultureSurveyPage />;
      case 'wildlife-strikes':
        return <WildlifeStrikePage />;
      case 'report-builder':
        return <ReportBuilderPage />;
      case 'emergency-response':
        return <EmergencyResponsePage />;
      case 'lessons-learned':
        return <LessonsLearnedPage />;
      case 'gap-analysis':
        return <GapAnalysisPage />;
      case 'risk-radar':
        return <RiskRadarPage />;
      case 'regulatory-api':
        return <RegulatoryAPIPage />;
      case 'maintenance':
        return <MaintenancePage />;
      case 'crew-management':
        return <CrewManagementPage />;
      case 'iosa-audit':
        return <IOSAAuditPage />;
      case 'ai-query':
        return <AIQueryPage />;
      case 'predictive-maintenance':
        return <PredictiveMaintenancePage />;
      case 'gaca-audit':
        return <GACAAuditPage onNavigate={handleNavigate} />;
      case 'inspector-mode':
        return <InspectorModePage onExit={() => handleNavigate('gaca-audit')} />;
      case 'ae-engagement':
        return <AEEngagementPage />;
      case 'resource-adequacy':
        return <ResourceAdequacyPage />;
      case 'sms-implementation-plan':
        return <SMSImplementationPlanPage />;
      default:
        return <DashboardPage onNavigate={handleNavigate} />;
    }
  };

  return (
    <>
      <MainLayout 
        currentPage={currentPage} 
        onNavigate={handleNavigate}
        onOpenQuickReport={() => setShowMobileReport(true)}
      >
        {renderPage()}
      </MainLayout>
      
      {/* Guided Tour */}
      <GuidedTour />

      {/* First-run onboarding wizard — admins only, auto-shows when company profile missing */}
      {user?.role === 'admin' && <OnboardingWizard token={token} />}

      {/* Global voice-command listener (opt-in — user clicks the mic to enable) */}
      <VoiceCommandListener />

      {/* Floating Support / feedback widget — visible on every authenticated page */}
      <SupportWidget />
      
      {/* Mobile Hazard Report Modal */}
      {showMobileReport && (
        <MobileHazardReport
          onClose={() => setShowMobileReport(false)}
          onSuccess={handleQuickReportSuccess}
        />
      )}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <BUProvider>
                <OfflineProvider>
                  <PWAOfflineIndicator />
                  <AppContent />
                  <OfflineIndicator />
                  <PWAInstallPrompt />
                  <Toaster position="top-right" richColors />
                </OfflineProvider>
              </BUProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
